type HomepageBlogConfig = {
  ajaxUrl: string;
  nonce: string;
};

declare global {
  interface Window {
    inkosporb2cHomepageBlog?: HomepageBlogConfig;
  }
}

const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const animateOut = (element: HTMLElement): Promise<void> => {
  if (prefersReducedMotion()) return Promise.resolve();

  return element
    .animate(
      [
        { opacity: 1, transform: "translateY(0)" },
        { opacity: 0, transform: "translateY(10px)" },
      ],
      {
        duration: 180,
        easing: "cubic-bezier(0.4, 0, 1, 1)",
        fill: "forwards",
      },
    )
    .finished.then(() => undefined)
    .catch(() => undefined);
};

const animateIn = (element: HTMLElement): void => {
  if (prefersReducedMotion()) {
    element.style.opacity = "";
    element.style.transform = "";
    return;
  }

  element.animate(
    [
      { opacity: 0, transform: "translateY(18px)" },
      { opacity: 1, transform: "translateY(0)" },
    ],
    {
      duration: 420,
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      fill: "both",
    },
  );
};

const getCategoryPills = (categoryBar: HTMLElement): HTMLElement[] =>
  Array.from(
    categoryBar.querySelectorAll<HTMLElement>("[data-homepage-blog-category]"),
  );

const scrollCategoryPillToStart = (
  categoryBar: HTMLElement,
  pill: HTMLElement,
): void => {
  const barRect = categoryBar.getBoundingClientRect();
  const pillRect = pill.getBoundingClientRect();
  const targetScroll = categoryBar.scrollLeft + pillRect.left - barRect.left;

  categoryBar.scrollTo({
    left: targetScroll,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
};

const getCategoryPillAtStartIndex = (
  categoryBar: HTMLElement,
  pills: HTMLElement[],
): number => {
  const barLeft = categoryBar.getBoundingClientRect().left;
  const tolerance = 2;

  return pills.reduce((currentIndex, pill, index) => {
    const pillLeft = pill.getBoundingClientRect().left;

    return pillLeft <= barLeft + tolerance ? index : currentIndex;
  }, 0);
};

const initCategoryBarArrows = (
  section: HTMLElement,
  categoryBar: HTMLElement,
): void => {
  const prevButton = section.querySelector<HTMLElement>(".hp-blog-mobile-prev");
  const nextButton = section.querySelector<HTMLElement>(".hp-blog-mobile-next");

  if (!prevButton || !nextButton) return;

  const scrollToSiblingPill = (direction: "prev" | "next"): void => {
    const pills = getCategoryPills(categoryBar);
    if (!pills.length) return;

    const currentIndex = getCategoryPillAtStartIndex(categoryBar, pills);
    const targetIndex =
      direction === "next"
        ? Math.min(pills.length - 1, currentIndex + 1)
        : Math.max(0, currentIndex - 1);

    scrollCategoryPillToStart(categoryBar, pills[targetIndex]);
  };

  prevButton.addEventListener("click", () => scrollToSiblingPill("prev"));
  nextButton.addEventListener("click", () => scrollToSiblingPill("next"));
};

export const initHomepageBlog = (): void => {
  const config = window.inkosporb2cHomepageBlog;

  if (!config || !window.fetch) return;

  document.querySelectorAll<HTMLElement>(".js-homepage-blog").forEach((section) => {
    if (section.dataset.homepageBlogInitialized === "true") return;

    const results = section.querySelector<HTMLElement>(".js-homepage-blog-results");
    const categoryBar = section.querySelector<HTMLElement>(".bl-listing-category-bar");
    let request: AbortController | undefined;

    if (!results || !categoryBar) return;

    section.dataset.homepageBlogInitialized = "true";
    initCategoryBarArrows(section, categoryBar);

    const syncActiveCategory = (category: string): void => {
      categoryBar
        .querySelectorAll<HTMLElement>("[data-homepage-blog-category]")
        .forEach((link) => {
          link.classList.toggle(
            "--active",
            (link.dataset.homepageBlogCategory || "") === category,
          );
        });
    };

    const loadCategory = async (category: string): Promise<void> => {
      request?.abort();
      request = new AbortController();

      section.setAttribute("aria-busy", "true");
      section.classList.add("is-loading");

      const formData = new FormData();
      formData.append("action", "inkosporb2c_homepage_blog");
      formData.append("nonce", config.nonce);
      formData.append("category", category);

      try {
        const response = await fetch(config.ajaxUrl, {
          method: "POST",
          body: formData,
          credentials: "same-origin",
          signal: request.signal,
        });
        const payload = await response.json();

        if (!response.ok || !payload.success) {
          throw new Error("Homepage blog request failed.");
        }

        await animateOut(results);
        results.innerHTML = payload.data.html;
        section.classList.remove("has-error");
        syncActiveCategory(category);
        animateIn(results);

        window.dispatchEvent(
          new CustomEvent("inkosporb2c:homepage-blog-updated", {
            detail: { root: results },
          }),
        );
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        section.classList.add("has-error");
      } finally {
        section.removeAttribute("aria-busy");
        section.classList.remove("is-loading");
      }
    };

    categoryBar.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest<HTMLElement>("[data-homepage-blog-category]");

      if (
        !link ||
        !categoryBar.contains(link) ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      loadCategory(link.dataset.homepageBlogCategory || "");
    });
  });
};
